import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';

export type ResumeReason = 'focus' | 'visibilitychange' | 'pageshow';
export type SuspendReason = 'blur' | 'visibilitychange' | 'pagehide';

// Отслеживает события фокуса, видимости и страницы, чтобы определить, когда приложение было в фоне и когда возобновлялось.
@Injectable({
  providedIn: 'root',
})
export class AppResumeLifecycleService {
  private readonly browserApiSupportService: BrowserApiSupportService = inject(BrowserApiSupportService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);
  private readonly listenerUnsubscribers: Array<() => void> = [];

  private readonly resumeSubject: BehaviorSubject<ResumeReason | null> = new BehaviorSubject<ResumeReason | null>(null);
  private readonly suspendSubject: BehaviorSubject<SuspendReason | null> = new BehaviorSubject<SuspendReason | null>(
    null
  );

  private readonly resumeDeduplicationWindowMs: number = 1500;

  private lastSuspendAt: number = 0;
  private lastResumeAt: number = 0;
  private lastBackgroundDurationMs: number | null = null;
  private isInitialized: boolean = false;

  public readonly onResume$: Observable<ResumeReason | null> = this.resumeSubject.asObservable();
  public readonly onSuspend$: Observable<SuspendReason | null> = this.suspendSubject.asObservable();

  constructor() {
    this.destroyRef.onDestroy(() => this.teardownListeners());
  }

  public init(): void {
    if (this.isInitialized || !this.browserApiSupportService.isBrowser()) {
      return;
    }

    this.isInitialized = true;

    this.listenerUnsubscribers.push(
      this.renderer.listen('window', 'focus', () => this.emitResume('focus')),
      this.renderer.listen('window', 'blur', () => this.emitSuspend('blur')),
      this.renderer.listen('window', 'pageshow', () => this.emitResume('pageshow')),
      this.renderer.listen('window', 'pagehide', () => this.emitSuspend('pagehide')),
      this.renderer.listen(this.document, 'visibilitychange', () => this.handleVisibilityChange())
    );
  }

  public getLastBackgroundDurationMs(): number | null {
    return this.lastBackgroundDurationMs;
  }

  private handleVisibilityChange(): void {
    if (this.document.visibilityState === 'hidden') {
      this.emitSuspend('visibilitychange');
      return;
    }

    this.emitResume('visibilitychange');
  }

  private emitSuspend(reason: SuspendReason): void {
    this.lastSuspendAt = Date.now();
    this.suspendSubject.next(reason);
  }

  private emitResume(reason: ResumeReason): void {
    const now: number = Date.now();
    const hasSuspendMarker: boolean = this.lastSuspendAt > 0;

    if (now - this.lastResumeAt < this.resumeDeduplicationWindowMs) {
      return;
    }

    if ((reason === 'focus' || reason === 'pageshow') && !hasSuspendMarker) {
      return;
    }

    // Для iOS/PWA цепочка событий может быть неполной, поэтому допускаем resume без suspend.
    this.lastBackgroundDurationMs = hasSuspendMarker ? now - this.lastSuspendAt : null;
    this.lastSuspendAt = 0;
    this.lastResumeAt = now;
    this.resumeSubject.next(reason);
  }

  private teardownListeners(): void {
    for (const unsubscribe of this.listenerUnsubscribers) {
      unsubscribe();
    }

    this.listenerUnsubscribers.length = 0;
  }
}
